import { Frequency } from '@generated/graphql';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { showsRequiredFieldError } from '../depositFieldErrors';

type ScheduleOption = {
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositEftScheduleSelectProp = {
  schedule?: Frequency;
  defaultScheduleOption?: Frequency | null;
  option?: ScheduleOption;
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  scheduleError: string;
};

export const DepositEftScheduleSelect = ({
  schedule,
  defaultScheduleOption,
  option,
  focused,
  onFocus,
  onChange,
  scheduleError,
}: DepositEftScheduleSelectProp) => {
  const { t } = useTranslation('workflowCompletions');
  const { translateBackendString } = useLocalization();

  return (
    <SelectField
      fullWidth
      sx={{ mb: 2 }}
      testId='schedule-select'
      value={schedule ?? defaultScheduleOption}
      label={translateBackendString(option?.label)}
      onChange={(e: any) => onChange({ schedule: e.target.value })}
      onBlur={() => onFocus('schedule')}
      error={showsRequiredFieldError({
        missing: !schedule,
        field: 'schedule',
        focused,
        requirement: option?.required,
      })}
      errorText={scheduleError}
    >
      <MenuItem
        data-testid='schedule-select-one-time'
        value={Frequency.OneTime}
      >
        {t('schedule.ONE_TIME')}
      </MenuItem>
      <MenuItem data-testid='schedule-select-weekly' value={Frequency.Weekly}>
        {t('schedule.WEEKLY')}
      </MenuItem>
      <MenuItem
        data-testid='schedule-select-bi-weekly'
        value={Frequency.BiWeekly}
      >
        {t('schedule.BI_WEEKLY')}
      </MenuItem>
      <MenuItem
        data-testid='schedule-select-semi-monthly'
        value={Frequency.SemiMonthly}
      >
        {t('schedule.SEMI_MONTHLY')}
      </MenuItem>
      <MenuItem data-testid='schedule-select-monthly' value={Frequency.Monthly}>
        {t('schedule.MONTHLY')}
      </MenuItem>
      <MenuItem
        data-testid='schedule-select-quarterly'
        value={Frequency.Quarterly}
      >
        {t('schedule.QUARTERLY')}
      </MenuItem>
    </SelectField>
  );
};
